import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Fab,
  Paper,
  Typography,
  IconButton,
  TextField,
  Avatar,
  CircularProgress,
  Collapse,
  Tooltip,
  Chip,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import BuildIcon from '@mui/icons-material/Build';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import { aiAssistantService } from '../../services/aiAssistantService';
import { useNavigate } from 'react-router-dom';
import { useObras } from '../../context/ObrasContext';
import { useTareas } from '../../context/TareasContext';
// Removido import de legacy SDK
// import { FunctionCall, Tool, SchemaType } from '@google/generative-ai';
import { collection, getDocs, query, where, limit as firestoreLimit } from 'firebase/firestore';
import { db } from '../../config/firebase';

// ─── Types ──────────────────────────────────────────────────────────

interface Message {
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
}

// Maximum results per DB query to control token usage
const MAX_RESULTS = 25;

// ─── Tool-name → friendly label mapping ─────────────────────────────

const TOOL_LABELS: Record<string, string> = {
  consultarBaseDeDatos: 'Consultando base de datos…',
  agregarTarea: 'Agregando tarea…',
  completarTarea: 'Completando tarea…',
  abrirDocumentos: 'Abriendo documentos…',
  navegarSeccion: 'Navegando…',
};

// ─── Tool declarations ──────────────────────────────────────────────

const aiTools: any[] = [
  {
    functionDeclarations: [
      {
        name: 'consultarBaseDeDatos',
        description:
          'Obtiene información de la base de datos de Mat ByB. ' +
          'Úsalo para responder preguntas sobre obras, tareas o documentos.',
        parameters: {
          type: 'OBJECT',
          properties: {
            entidad: {
              type: 'STRING',
              description:
                'La entidad a consultar: "obras", "tareas" o "documentos".',
            },
            filtroObra: {
              type: 'STRING',
              description:
                'Nombre parcial de la obra para filtrar resultados. Opcional.',
            },
            soloIncompletas: {
              type: 'STRING',
              description:
                'Si es "true", para tareas solo devuelve las no completadas. Por defecto "true".',
            },
          },
          required: ['entidad'],
        },
      },
      {
        name: 'agregarTarea',
        description:
          'Crea una nueva tarea y la asocia a una obra. ' +
          'Usa esto cuando el usuario pida agregar o crear una tarea.',
        parameters: {
          type: 'OBJECT',
          properties: {
            nombreTarea: {
              type: 'STRING',
              description: 'El nombre descriptivo de la tarea.',
            },
            nombreObra: {
              type: 'STRING',
              description:
                'El nombre de la obra a la que pertenece la tarea. ' +
                'Intenta deducirlo del contexto si no se especifica.',
            },
          },
          required: ['nombreTarea', 'nombreObra'],
        },
      },
      {
        name: 'completarTarea',
        description:
          'Marca una tarea existente como completada. ' +
          'Usa esto cuando el usuario indique que terminó una tarea.',
        parameters: {
          type: 'OBJECT',
          properties: {
            nombreTarea: {
              type: 'STRING',
              description:
                'El nombre de la tarea a completar. Busca la coincidencia más cercana.',
            },
          },
          required: ['nombreTarea'],
        },
      },
      {
        name: 'abrirDocumentos',
        description:
          'Abre la sección de documentos para ver planos y archivos. ' +
          'Úsalo cuando pidan "mostrar el plano", "abrir documento", etc.',
        parameters: {
          type: 'OBJECT',
          properties: {
            query: {
              type: 'STRING',
              description: 'Término de búsqueda opcional para el documento.',
            },
          },
        },
      },
      {
        name: 'navegarSeccion',
        description:
          'Navega a una sección de la aplicación. Secciones disponibles: ' +
          'inicio, obras, tareas, materiales, documentos, fotos, herramientas, configuracion, presupuesto.',
        parameters: {
          type: 'OBJECT',
          properties: {
            seccion: {
              type: 'STRING',
              description: 'Nombre de la sección a la que navegar.',
            },
          },
          required: ['seccion'],
        },
      },
    ],
  },
];

// ─── Section route mapping ──────────────────────────────────────────

const SECTION_ROUTES: Record<string, string> = {
  inicio: '/',
  home: '/',
  obras: '/obras',
  tareas: '/tareas',
  materiales: '/materiales',
  documentos: '/documentos',
  fotos: '/fotos',
  herramientas: '/herramientas',
  configuracion: '/configuracion',
  presupuesto: '/presupuesto',
};

// ─── Component ──────────────────────────────────────────────────────

export const AIAssistantWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfigured, setIsConfigured] = useState(aiAssistantService.isConfigured());
  const [toolActivity, setToolActivity] = useState<string | null>(null);
  const [isListening, setIsListening] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);

  const navigate = useNavigate();
  const { obras, obraSeleccionada } = useObras();
  const { tareas, agregarTarea, completarTarea } = useTareas();

  // ── Scroll management ──

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen, scrollToBottom]);

  // ── Initialize chat only once (not on every open) ──

  useEffect(() => {
    if (isOpen) {
      const configured = aiAssistantService.isConfigured();
      setIsConfigured(configured);

      if (configured && !aiAssistantService.hasChatSession()) {
        aiAssistantService.startChat([], aiTools);
      }
    }
  }, [isOpen]);

  // ── Tool activity callback (for UI feedback) ──

  const handleToolActivity = useCallback((toolName: string) => {
    setToolActivity(TOOL_LABELS[toolName] || `Ejecutando ${toolName}…`);
  }, []);

  // ── Voice input (Web Speech API) ──

  const voiceSupported = typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  const startListening = useCallback(() => {
    if (!voiceSupported || isListening) return;

    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    const recognition = new SpeechRecognition();
    recognition.lang = 'es-AR';
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.continuous = false;

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = (event: any) => {
      const transcript = Array.from(event.results)
        .map((result: any) => result[0].transcript)
        .join('');
      setInput(transcript);
    };

    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);

    recognitionRef.current = recognition;
    recognition.start();
  }, [voiceSupported, isListening]);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  // ── Tool dispatcher ──

  const toolDispatcher = useCallback(
    async (call: any) => {
      const args = (call.args ?? {}) as Record<string, string>;

      switch (call.name) {
        // ─────────── CONSULTAR BASE DE DATOS ───────────
        case 'consultarBaseDeDatos': {
          const { entidad, filtroObra, soloIncompletas } = args;

          if (entidad === 'obras') {
            let resultado = obras.map(o => ({
              nombre: o.nombre,
              id: o.id,
              estado: o.estado,
              ubicacion: o.ubicacion,
            }));

            if (filtroObra) {
              resultado = resultado.filter(o =>
                o.nombre.toLowerCase().includes(filtroObra.toLowerCase())
              );
            }

            return { resultado: resultado.slice(0, MAX_RESULTS), total: obras.length };
          }

          if (entidad === 'tareas') {
            let filteredTareas = [...tareas];

            // Filter by obra if specified or if there is a selected obra
            if (filtroObra) {
              filteredTareas = filteredTareas.filter(t =>
                t.obraNombre?.toLowerCase().includes(filtroObra.toLowerCase())
              );
            } else if (obraSeleccionada) {
              filteredTareas = filteredTareas.filter(t => t.obraId === obraSeleccionada);
            }

            // Filter only incomplete by default
            if (soloIncompletas !== 'false') {
              filteredTareas = filteredTareas.filter(t => !t.completada);
            }

            const resultado = filteredTareas.slice(0, MAX_RESULTS).map(t => ({
              nombre: t.nombre,
              completada: t.completada,
              obra: t.obraNombre,
              prioridad: t.prioridad || 'sin definir',
              esUrgente: t.esUrgente || false,
              fechaProgramada: t.fechaProgramada
                ? (t.fechaProgramada instanceof Date
                    ? t.fechaProgramada.toLocaleDateString()
                    : new Date(t.fechaProgramada).toLocaleDateString())
                : 'sin fecha',
            }));

            return {
              resultado,
              total: filteredTareas.length,
              mostrando: resultado.length,
            };
          }

          if (entidad === 'documentos') {
            try {
              const documentosRef = collection(db, 'documentos');
              let q;

              // Filter by selected obra if available
              if (filtroObra) {
                const matchingObra = obras.find(o =>
                  o.nombre.toLowerCase().includes(filtroObra.toLowerCase())
                );
                if (matchingObra) {
                  q = query(documentosRef, where('obra', '==', matchingObra.id), firestoreLimit(MAX_RESULTS));
                } else {
                  q = query(documentosRef, firestoreLimit(MAX_RESULTS));
                }
              } else if (obraSeleccionada) {
                q = query(documentosRef, where('obra', '==', obraSeleccionada), firestoreLimit(MAX_RESULTS));
              } else {
                q = query(documentosRef, firestoreLimit(MAX_RESULTS));
              }

              const snap = await getDocs(q);
              const docs = snap.docs.map(docSnap => {
                const data = docSnap.data();
                // Resolve obra name from the obras array
                const obraName = obras.find(o => o.id === (data.obra || data.obraId))?.nombre || 'Sin obra';
                return {
                  nombre: data.nombre,
                  descripcion: data.descripcion || '',
                  tipo: data.tipo || 'Otro',
                  obra: obraName,
                };
              });
              return { resultado: docs, total: snap.size };
            } catch (e: any) {
              return { error: 'No se pudieron cargar los documentos: ' + e.message };
            }
          }

          return {
            error: 'Entidad desconocida. Por favor consulta "obras", "tareas" o "documentos".',
          };
        }

        // ─────────── AGREGAR TAREA ───────────
        case 'agregarTarea': {
          const { nombreTarea, nombreObra } = args;
          const targetObra = obras.find(o =>
            o.nombre.toLowerCase().includes(nombreObra.toLowerCase())
          );
          if (!targetObra) {
            return {
              error: `No se encontró ninguna obra que coincida con "${nombreObra}". Obras disponibles: ${obras.map(o => o.nombre).join(', ')}`,
            };
          }

          try {
            await agregarTarea(nombreTarea, new Date(), targetObra.id);
            return {
              exito: true,
              mensaje: `Tarea '${nombreTarea}' agregada exitosamente a la obra '${targetObra.nombre}'.`,
            };
          } catch (e: any) {
            return { error: `Error al agregar tarea: ${e.message}` };
          }
        }

        // ─────────── COMPLETAR TAREA ───────────
        case 'completarTarea': {
          const { nombreTarea } = args;
          const pendingTasks = tareas.filter(t => !t.completada);
          const targetTask = pendingTasks.find(t =>
            t.nombre.toLowerCase().includes(nombreTarea.toLowerCase())
          );

          if (!targetTask) {
            // Provide helpful context when the task is not found
            const available = pendingTasks.slice(0, 10).map(t => t.nombre);
            return {
              error: `No se encontró ninguna tarea pendiente que coincida con "${nombreTarea}".`,
              tareasPendientes: available,
            };
          }

          try {
            await completarTarea(targetTask.id);
            return {
              exito: true,
              mensaje: `Tarea '${targetTask.nombre}' marcada como completada.`,
            };
          } catch (e: any) {
            return { error: `Error al completar tarea: ${e.message}` };
          }
        }

        // ─────────── ABRIR DOCUMENTOS ───────────
        case 'abrirDocumentos': {
          navigate('/documentos');
          return {
            exito: true,
            mensaje: 'Se abrió la pestaña de Documentos.',
          };
        }

        // ─────────── NAVEGAR SECCIÓN ───────────
        case 'navegarSeccion': {
          const { seccion } = args;
          const route = SECTION_ROUTES[seccion.toLowerCase()];
          if (!route) {
            return {
              error: `Sección "${seccion}" no reconocida. Secciones disponibles: ${Object.keys(SECTION_ROUTES).join(', ')}`,
            };
          }
          navigate(route);
          return {
            exito: true,
            mensaje: `Se navegó a la sección "${seccion}".`,
          };
        }

        default:
          return { error: `Función '${call.name}' no implementada.` };
      }
    },
    [obras, tareas, obraSeleccionada, agregarTarea, completarTarea, navigate]
  );

  // ── Send message ──

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      text: input,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setToolActivity(null);

    try {
      const response = await aiAssistantService.sendMessage(
        input,
        toolDispatcher,
        handleToolActivity
      );
      const aiMessage: Message = {
        text: response,
        sender: 'ai',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error: any) {
      const errorMessage: Message = {
        text: error.message || 'Lo siento, hubo un error al procesar tu solicitud.',
        sender: 'ai',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setToolActivity(null);
    }
  };

  const clearChat = () => {
    setMessages([]);
    aiAssistantService.startChat([], aiTools);
  };

  // ── Render ──

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: { xs: 80, sm: 24 },
        right: 24,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
      }}
    >
      <Collapse in={isOpen}>
        <Paper
          elevation={6}
          sx={{
            width: { xs: 'calc(100vw - 48px)', sm: 370 },
            height: 480,
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {/* ── Header ── */}
          <Box
            sx={{
              p: 1.5,
              px: 2,
              bgcolor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartToyIcon fontSize="small" />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                Asistente IA
              </Typography>
            </Box>
            <Box>
              <Tooltip title="Limpiar chat">
                <IconButton size="small" onClick={clearChat} sx={{ color: 'white', mr: 0.5 }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cerrar">
                <IconButton size="small" onClick={() => setIsOpen(false)} sx={{ color: 'white' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* ── Messages ── */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              p: 2,
              bgcolor: '#f8fafc',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            {!isConfigured && (
              <Box sx={{ textAlign: 'center', mt: 4, px: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  El asistente no está configurado. Por favor, ingresa tu API Key en la sección de Configuración.
                </Typography>
              </Box>
            )}

            {messages.length === 0 && isConfigured && (
              <Box sx={{ textAlign: 'center', mt: 4, px: 2 }}>
                <Avatar sx={{ mx: 'auto', mb: 1, bgcolor: 'primary.light', width: 48, height: 48 }}>
                  <SmartToyIcon />
                </Avatar>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  ¡Hola! Soy tu asistente de IA.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Puedo consultar obras, tareas y documentos, agregar o completar tareas, y navegar por la app.
                </Typography>
              </Box>
            )}

            {messages.map((msg, index) => (
              <Box
                key={index}
                sx={{
                  alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                }}
              >
                <Paper
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: msg.sender === 'user' ? 'primary.main' : 'white',
                    color: msg.sender === 'user' ? 'white' : 'text.primary',
                    borderTopRightRadius: msg.sender === 'user' ? 0 : 16,
                    borderTopLeftRadius: msg.sender === 'ai' ? 0 : 16,
                    boxShadow: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {msg.text}
                  </Typography>
                </Paper>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    mt: 0.25,
                    display: 'block',
                    textAlign: msg.sender === 'user' ? 'right' : 'left',
                    fontSize: '0.65rem',
                  }}
                >
                  {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>
            ))}

            {/* Loading indicator with tool activity feedback */}
            {isLoading && (
              <Box sx={{ alignSelf: 'flex-start', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">
                    {toolActivity || 'Gemini está pensando…'}
                  </Typography>
                </Box>
                {toolActivity && (
                  <Chip
                    icon={<BuildIcon sx={{ fontSize: 14 }} />}
                    label={toolActivity}
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{ fontSize: '0.7rem', height: 22 }}
                  />
                )}
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* ── Input ── */}
          <Box sx={{ p: 1.5, bgcolor: 'white', borderTop: '1px solid', borderColor: 'divider' }}>
            <TextField
              fullWidth
              size="small"
              placeholder={isListening ? 'Escuchando…' : 'Escribe o habla un mensaje…'}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={isLoading || !isConfigured}
              sx={{
                '& .MuiOutlinedInput-root': isListening ? {
                  boxShadow: '0 0 0 2px #ef4444',
                  borderRadius: 1,
                } : {},
              }}
              InputProps={{
                endAdornment: (
                  <Box sx={{ display: 'flex', gap: 0.25 }}>
                    {/* Mic button */}
                    {voiceSupported && (
                      <Tooltip title={isListening ? 'Detener grabación' : 'Hablar'}>
                        <IconButton
                          size="small"
                          onClick={isListening ? stopListening : startListening}
                          disabled={isLoading || !isConfigured}
                          sx={{
                            color: isListening ? 'error.main' : 'text.secondary',
                            animation: isListening ? 'pulse 1.2s ease-in-out infinite' : 'none',
                            '@keyframes pulse': {
                              '0%, 100%': { opacity: 1 },
                              '50%': { opacity: 0.4 },
                            },
                          }}
                        >
                          {isListening ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {/* Send button */}
                    <Tooltip title="Enviar">
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleSend}
                          disabled={!input.trim() || isLoading || !isConfigured}
                          color="primary"
                        >
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                ),
              }}
            />
          </Box>
        </Paper>
      </Collapse>

      <Fab
        color="primary"
        aria-label="asistente"
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          background: 'linear-gradient(45deg, #1E40AF 30%, #3B82F6 90%)',
          boxShadow: '0 4px 20px rgba(37, 99, 235, 0.4)',
          transition: 'transform 0.2s',
          '&:hover': {
            transform: 'scale(1.1)',
          },
        }}
      >
        {isOpen ? <CloseIcon /> : <SmartToyIcon />}
      </Fab>
    </Box>
  );
};
